import React, { useState } from "react";
import { api } from "./api";

// 3 steps: email → mfa code → password
// expiredSession: boolean passed from App.js — shows a banner when the previous
// session timed out rather than just showing the login form cold.
export default function LoginFlow({ onLogin, expiredSession }) {
  const [step, setStep] = useState("email"); // "email" | "mfa" | "password"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { sessionId, mfaRequired } = await api.authStart(email);
      setSessionId(sessionId);
      setStep(mfaRequired === false ? "password" : "mfa");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.authVerify(sessionId, code);
      setStep("password");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await api.authComplete(sessionId, password);
      onLogin(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    setError(null);
    setCode("");
    setPassword("");
    setSessionId(null);
    setStep("email");
  }

  return (
    <div className="login-page">
      <div className="login-card">

        <div className="login-logo">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <rect width="36" height="36" rx="9" fill="#060a14"/>
            <circle cx="18" cy="18" r="10" stroke="#E63946" strokeWidth="2"/>
            <circle cx="18" cy="18" r="5" stroke="#E63946" strokeWidth="1.5"/>
            <line x1="18" y1="4" x2="18" y2="8" stroke="#E63946" strokeWidth="2" strokeLinecap="round"/>
            <line x1="18" y1="28" x2="18" y2="32" stroke="#E63946" strokeWidth="2" strokeLinecap="round"/>
            <line x1="4" y1="18" x2="8" y2="18" stroke="#E63946" strokeWidth="2" strokeLinecap="round"/>
            <line x1="28" y1="18" x2="32" y2="18" stroke="#E63946" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="18" cy="18" r="2" fill="#E63946"/>
          </svg>
          <span className="login-title">Scouting Nexus</span>
        </div>

        {/* Expiry banner — only shown when returning with an expired token */}
        {expiredSession && step === "email" && (
          <div className="expiry-notice">
            Your session has expired. Please sign in again.
          </div>
        )}

        <div className="step-indicators">
          {["email","mfa","password"].map((s, i) => (
            <div key={s} className={`step-dot ${step === s ? "active" : ["email","mfa","password"].indexOf(step) > i ? "done" : ""}`} />
          ))}
        </div>

        {/* Step 1: Email */}
        {step === "email" && (
          <>
            <p className="login-subtitle">Sign in with your GameChanger account.</p>
            <form onSubmit={handleEmailSubmit} className="login-form">
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
              {error && <div className="error-msg">{error}</div>}
              <button type="submit" className="btn-primary" disabled={loading || !email}>
                {loading ? "Sending code…" : "Continue →"}
              </button>
            </form>
          </>
        )}

        {/* Step 2: MFA code */}
        {step === "mfa" && (
          <>
            <p className="login-subtitle">
              We sent a verification code to <strong>{email}</strong>. Enter it below.
            </p>
            <form onSubmit={handleMfaSubmit} className="login-form">
              <div className="field">
                <label>Verification code</label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="123456"
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="input-code"
                />
              </div>
              {error && <div className="error-msg">{error}</div>}
              <button type="submit" className="btn-primary" disabled={loading || !code}>
                {loading ? "Verifying…" : "Verify →"}
              </button>
              <button type="button" className="btn-text" onClick={handleBack}>
                ← Use a different email
              </button>
            </form>
          </>
        )}

        {/* Step 3: Password */}
        {step === "password" && (
          <>
            <p className="login-subtitle">Enter your GameChanger password.</p>
            <form onSubmit={handlePasswordSubmit} className="login-form">
              <div className="field">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoFocus
                  autoComplete="current-password"
                />
              </div>
              {error && <div className="error-msg">{error}</div>}
              <button type="submit" className="btn-primary" disabled={loading || !password}>
                {loading ? "Signing in…" : "Sign in →"}
              </button>
              <button type="button" className="btn-text" onClick={handleBack}>
                ← Start over
              </button>
            </form>
          </>
        )}

        <p className="login-note">
          Your credentials are sent directly to GameChanger and are never stored on our servers.
        </p>
        <p className="login-note" style={{marginTop:8}}>
          A <a href="https://gc.com/pricing" target="_blank" rel="noreferrer" style={{color:'var(--text-2)',textDecoration:'underline'}}>GameChanger Plus</a> subscription is required to access stats.
        </p>
      </div>
    </div>
  );
}
